import { DEFAULT_LOCALE, type Locale } from "./config";

// Namespace dictionaries. JSON namespaces are small (a few KB gzipped each) and
// are code-split per locale directory by Vite, so a page only pays for the
// namespaces it actually reads through `useTranslation`.
const modules = import.meta.glob<Record<string, unknown>>("./locales/*/*.json", {
  eager: true,
  import: "default",
});

type Dict = Record<string, unknown>;

const REGISTRY: Record<string, Record<string, Dict>> = {};
for (const [path, mod] of Object.entries(modules)) {
  const m = /\.\/locales\/([^/]+)\/([^/]+)\.json$/.exec(path);
  if (!m) continue;
  const [, locale, ns] = m;
  REGISTRY[locale] ??= {};
  REGISTRY[locale][ns] = mod as Dict;
}

export function namespaceDict(locale: Locale, ns: string): Dict {
  return REGISTRY[locale]?.[ns] ?? {};
}

function lookup(dict: Dict, key: string): unknown {
  let cur: unknown = dict;
  for (const part of key.split(".")) {
    if (cur == null || typeof cur !== "object") return undefined;
    cur = (cur as Dict)[part];
  }
  return cur;
}

export type TVars = Record<string, string | number>;

/** `{count, plural, one {# bid} other {# bids}}` — a minimal ICU plural subset. */
function applyPlural(template: string, vars: TVars, locale: Locale): string {
  return template.replace(
    /\{(\w+),\s*plural,\s*([\s\S]*?)\}\s*(?=$|[^}])/g,
    (whole, name: string, body: string) => {
      const value = Number(vars[name]);
      if (!Number.isFinite(value)) return whole;
      const forms: Record<string, string> = {};
      const re = /(zero|one|two|few|many|other|=\d+)\s*\{([^{}]*)\}/g;
      let m: RegExpExecArray | null;
      while ((m = re.exec(body))) forms[m[1]] = m[2];
      const exact = forms[`=${value}`];
      const category = new Intl.PluralRules(locale).select(value);
      const chosen = exact ?? forms[category] ?? forms.other ?? "";
      return chosen.replace(/#/g, new Intl.NumberFormat(locale).format(value));
    },
  );
}

function interpolate(template: string, vars: TVars | undefined, locale: Locale): string {
  let out = template;
  if (vars) out = applyPlural(out, vars, locale);
  if (!vars) return out;
  return out.replace(/\{(\w+)\}/g, (whole, name: string) => {
    const v = vars[name];
    if (v === undefined) return whole;
    return typeof v === "number" ? new Intl.NumberFormat(locale).format(v) : String(v);
  });
}

/**
 * Resolves a key with a three-step fallback: active locale -> English ->
 * the inline English default supplied by the caller. Raw keys are never
 * shown to users.
 */
export function translate(
  locale: Locale,
  ns: string,
  key: string,
  defaultText?: string,
  vars?: TVars,
): string {
  const primary = lookup(namespaceDict(locale, ns), key);
  const value =
    typeof primary === "string"
      ? primary
      : (() => {
          const fallback = lookup(namespaceDict(DEFAULT_LOCALE, ns), key);
          return typeof fallback === "string" ? fallback : (defaultText ?? "");
        })();
  return interpolate(value, vars, locale);
}
